import { createAction } from '@reduxjs/toolkit'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import { computePopupPosition, getFlagScreenBounds } from '../effects/adjustFlagDetailsPopupPosition'
import { fetchFlagComments } from '../../../api/projects-api'
import { FLAG_COLORS, FLAG_STROKE_WIDTH } from '../../../constants/flagStyle'
import { FlagComments, FlagEnum } from '../../../models/flags'
import { DEFAULT_DIALOG_HEIGHT } from '../../../shared/constants/sizes'
import managers from '../../lib/managers'
import { Workspace } from '../../lib/toolBoxes/2D'
import { setLoading } from '../../slices/common'
import {
    selectIsFlagFormOpen,
    setActiveFlag,
    setActiveFlagComments,
    setPendingFlagSwitch,
    showFlagDetails,
} from '../../slices/materialFlags'
import { setDisableFlagTool } from '../../slices/tools'

export const setActiveFlagAction = createAction<{ flag_id: number; paperId?: number }>('setActiveFlagAction')

export function* handleSetActiveFlag(action: ReturnType<typeof setActiveFlagAction>) {
    const paperManager = yield call(managers.get2DManager)

    if (!paperManager) return

    const { flag_id, paperId } = action.payload

    const workspaceTool: Workspace = yield call(paperManager.getTool, Workspace.NAME)

    const items: paper.Item[] | null = yield call(
        workspaceTool.getItemsWithCriteria,
        'data',
        (data) => data.flag_id === flag_id
    )

    if (items) {
        items.forEach((item) => {
            if (item?.children?.[0]) {
                const flagType = item.data.type || FlagEnum.TASK

                item.children[0].strokeColor =
                    FLAG_COLORS[flagType]?.borderColor || FLAG_COLORS[FlagEnum.TASK].borderColor
                item.children[0].strokeWidth = FLAG_STROKE_WIDTH
            }
        })
    }

    const isFlagFormOpen = yield select(selectIsFlagFormOpen)

    if (isFlagFormOpen) {
        yield put(setPendingFlagSwitch({ flagId: flag_id, paperId }))

        return
    }

    // get clicked item bounds
    const result = yield call(getFlagScreenBounds, workspaceTool, (id: number) => id === paperId)

    if (result?.openingLinkId) {
        const { paperId, openingLinkId, flagBounds, projectView } = result
        const { top, left } = computePopupPosition(flagBounds, projectView.element as HTMLCanvasElement)

        yield put(
            showFlagDetails({
                flagId: flag_id,
                openingLinkId,
                position: { x: left, y: top },
                dialogHeight: DEFAULT_DIALOG_HEIGHT,
                paperId,
            })
        )
    }

    yield put(setDisableFlagTool(true))
    yield put(setActiveFlag({ flag_id }))
    yield put(setLoading(true))

    const res: FlagComments[] = yield call(fetchFlagComments, flag_id)

    yield put(setActiveFlagComments({ comments: res }))
    yield put(setLoading(false))
}

export function* watchForSetActiveFlagAction() {
    yield takeLatest(setActiveFlagAction.type, handleSetActiveFlag)
}
